(() => {
  'use strict';

  const VERSION = '1.3.0';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const OPEN_SELECTOR = '[data-omri-size-guide-open]';
  const ROOT_SELECTOR = '[data-omri-size-guide]';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  if (window.OmriSizeGuide?.version === VERSION) {
    window.OmriSizeGuide.boot();
    return;
  }

  const state = {
    root: null,
    trigger: null,
    contentFrame: 0,
    positionFrame: 0,
    openFrame: 0,
    closeTimer: 0,
    closePanel: null,
    closeListener: null,
    finishClose: null,
    closing: false
  };

  const reducedMotion = () => window.matchMedia(REDUCED_MOTION).matches;
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function cancelMethodFrames() {
    if (state.contentFrame) window.cancelAnimationFrame(state.contentFrame);
    if (state.positionFrame) window.cancelAnimationFrame(state.positionFrame);
    state.contentFrame = 0;
    state.positionFrame = 0;
    state.root?.classList.remove('is-method-positioning');
  }

  function cancelOpenFocus() {
    if (state.openFrame) window.cancelAnimationFrame(state.openFrame);
    state.openFrame = 0;
  }

  function cancelClose() {
    if (state.closeTimer) window.clearTimeout(state.closeTimer);
    if (state.closePanel && state.closeListener) {
      state.closePanel.removeEventListener('transitionend', state.closeListener);
    }
    state.closeTimer = 0;
    state.closePanel = null;
    state.closeListener = null;
    state.finishClose = null;
    state.closing = false;
  }

  function stopVideos(scope = state.root) {
    if (!scope) return;
    qa('[data-omri-size-guide-media]', scope).forEach(media => {
      media.querySelector('.omri-size-guide__iframe')?.remove();
      media.classList.remove('is-playing');
    });
  }

  function methodFromTrigger(trigger) {
    const sizing = trigger?.closest?.('.omri-sizing-component');
    const select = sizing?.querySelector?.('[data-omri-sizing-method-select]');
    return select?.value === 'mm' ? 'mm' : 'kit';
  }

  function setTabs(method) {
    if (!state.root) return null;
    const methods = state.root.querySelector('.omri-size-guide__methods');
    if (methods) methods.dataset.active = method;
    let selected = null;
    qa('[data-omri-size-guide-tab]', state.root).forEach(tab => {
      const active = tab.dataset.omriSizeGuideTab === method;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active) selected = tab;
    });
    return selected;
  }

  function setContent(method) {
    if (!state.root) return;
    stopVideos(state.root);

    qa('[data-omri-size-guide-panel]', state.root).forEach(panel => {
      const active = panel.dataset.omriSizeGuidePanel === method;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    const scroll = state.root.querySelector('[data-omri-size-guide-scroll]');
    if (scroll) scroll.scrollTop = 0;
  }

  function selectMethod(method, { focusTab = false, animate = true } = {}) {
    if (!state.root) return;
    const root = state.root;
    const resolved = method === 'mm' ? 'mm' : 'kit';
    const shouldAnimate = animate && !reducedMotion();

    cancelMethodFrames();
    root.classList.toggle('is-method-positioning', !shouldAnimate);
    const selectedTab = setTabs(resolved);
    if (focusTab && selectedTab) selectedTab.focus({ preventScroll: true });

    // Aucun délai de remplacement : le contenu correspond déjà au nouvel onglet.
    setContent(resolved);
    if (!shouldAnimate) {
      root.classList.remove('is-method-switching');
      if (reducedMotion()) {
        root.classList.remove('is-method-positioning');
      } else {
        const methods = root.querySelector('.omri-size-guide__methods');
        if (methods) void methods.offsetWidth;
        state.positionFrame = window.requestAnimationFrame(() => {
          state.positionFrame = 0;
          if (state.root === root) root.classList.remove('is-method-positioning');
        });
      }
      return;
    }

    // Une seule entrée en fondu ; le nouveau panneau ne subit pas de fondu de sortie.
    root.classList.add('is-method-switching');
    const pane = root.querySelector('[data-omri-size-guide-panel="' + resolved + '"]');
    if (pane) void pane.offsetWidth;
    state.contentFrame = window.requestAnimationFrame(() => {
      state.contentFrame = 0;
      if (state.root === root && !state.closing) root.classList.remove('is-method-switching');
    });
  }

  function play(media) {
    if (!media || media.classList.contains('is-playing')) return;
    const embedUrl = String(media.dataset.embedUrl || '').trim();
    if (!embedUrl) return;

    const root = state.root;
    stopVideos(root);
    const iframe = document.createElement('iframe');
    iframe.className = 'omri-size-guide__iframe';
    iframe.src = embedUrl;
    iframe.title = media.dataset.videoTitle || 'Tutoriel vidéo';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    media.appendChild(iframe);
    media.classList.add('is-playing');
    window.requestAnimationFrame(() => {
      if (state.root === root && !state.closing && iframe.isConnected) {
        iframe.focus({ preventScroll: true });
      }
    });
  }

  function open(trigger) {
    boot();
    if (!state.root) return;

    const root = state.root;
    cancelClose();
    cancelOpenFocus();
    cancelMethodFrames();
    root.classList.remove('is-closing');
    state.trigger = trigger || state.trigger || document.activeElement;
    selectMethod(methodFromTrigger(trigger), { animate: false });
    root.setAttribute('aria-hidden', 'false');
    qa(OPEN_SELECTOR).forEach(button => button.setAttribute('aria-expanded', button === trigger ? 'true' : 'false'));
    document.documentElement.classList.add('omri-size-guide-open');

    const scroll = root.querySelector('[data-omri-size-guide-scroll]');
    if (scroll) scroll.scrollTop = 0;

    // Calculer l'état de départ avant de lancer le glissement, même au premier clic.
    const panel = root.querySelector('.omri-size-guide__panel');
    if (panel && !reducedMotion()) void panel.offsetWidth;
    root.classList.add('is-open');

    state.openFrame = window.requestAnimationFrame(() => {
      state.openFrame = 0;
      if (state.root === root && !state.closing && root.getAttribute('aria-hidden') === 'false') {
        root.querySelector('.omri-size-guide__close')?.focus({ preventScroll: true });
      }
    });
  }

  function close({ restoreFocus = true, immediate = false } = {}) {
    if (!state.root || state.root.getAttribute('aria-hidden') === 'true') return;
    if (state.closing) {
      if (immediate) state.finishClose?.({ restoreFocusOverride: restoreFocus });
      return;
    }

    const root = state.root;
    const trigger = state.trigger;
    const panel = root.querySelector('.omri-size-guide__panel');
    cancelMethodFrames();
    cancelOpenFocus();
    stopVideos(root);
    root.classList.remove('is-method-switching');
    qa(OPEN_SELECTOR).forEach(button => button.setAttribute('aria-expanded', 'false'));

    const finish = ({ restoreFocusOverride = restoreFocus } = {}) => {
      if (state.root !== root || !state.closing) return;
      cancelClose();
      root.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-closing');
      document.documentElement.classList.remove('omri-size-guide-open');
      state.trigger = null;
      if (restoreFocusOverride && trigger?.isConnected && typeof trigger.focus === 'function') {
        trigger.focus({ preventScroll: true });
      }
    };

    state.closing = true;
    state.finishClose = finish;
    root.classList.add('is-closing');

    const wasOpen = root.classList.contains('is-open');
    if (immediate || reducedMotion() || !panel || !wasOpen) {
      root.classList.remove('is-open');
      finish();
      return;
    }

    state.closePanel = panel;
    state.closeListener = event => {
      if (event.target === panel && event.propertyName === 'transform') finish();
    };
    panel.addEventListener('transitionend', state.closeListener);

    // aria-hidden et le verrouillage du scroll restent actifs pendant les 400 ms.
    root.classList.remove('is-open');
    state.closeTimer = window.setTimeout(finish, 450);
  }

  function trapFocus(event) {
    if (!state.root || state.root.getAttribute('aria-hidden') !== 'false') return;
    const panel = state.root.querySelector('.omri-size-guide__panel');
    if (!panel) return;

    const focusables = qa(FOCUSABLE, panel).filter(element => {
      if (element.tabIndex < 0 || element.closest('[hidden], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      return !element.hidden && style.display !== 'none' && style.visibility !== 'hidden';
    });
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleTabKey(tab, event) {
    const tabs = qa('[data-omri-size-guide-tab]', state.root);
    const index = tabs.indexOf(tab);
    if (index < 0) return;

    let next = null;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    selectMethod(tabs[next].dataset.omriSizeGuideTab, { focusTab: true, animate: true });
  }

  function boot() {
    const roots = qa(ROOT_SELECTOR);
    if (!roots.length) {
      close({ restoreFocus: false, immediate: true });
      state.root = null;
      return;
    }

    const inlineRoot = roots.slice().reverse().find(root => root.parentElement !== document.body);
    const root = inlineRoot || roots[0];

    if (state.root && state.root !== root) {
      close({ restoreFocus: false, immediate: true });
      stopVideos(state.root);
    }

    roots.forEach(candidate => {
      if (candidate !== root) candidate.remove();
    });

    if (root.parentElement !== document.body) document.body.appendChild(root);
    if (state.root === root) return;

    root.setAttribute('aria-hidden', 'true');
    root.classList.remove('is-open');
    root.classList.remove('is-closing');
    root.classList.remove('is-method-switching');
    state.root = root;
    selectMethod('kit', { animate: false });
  }

  function bindGlobalEvents() {
    document.addEventListener('click', event => {
      const opener = event.target.closest?.(OPEN_SELECTOR);
      if (opener) {
        event.preventDefault();
        open(opener);
        return;
      }

      if (!state.root) return;

      const closer = event.target.closest?.('[data-omri-size-guide-close]');
      if (closer && state.root.contains(closer)) {
        event.preventDefault();
        close();
        return;
      }

      if (state.closing || state.root.getAttribute('aria-hidden') !== 'false') return;

      const tab = event.target.closest?.('[data-omri-size-guide-tab]');
      if (tab && state.root.contains(tab)) {
        event.preventDefault();
        selectMethod(tab.dataset.omriSizeGuideTab, { animate: true });
        return;
      }

      const playButton = event.target.closest?.('[data-omri-size-guide-play]');
      if (playButton && state.root.contains(playButton)) {
        event.preventDefault();
        play(playButton.closest('[data-omri-size-guide-media]'));
      }
    });

    document.addEventListener('keydown', event => {
      if (!state.root || state.root.getAttribute('aria-hidden') !== 'false') return;

      if (event.key === 'Tab') {
        trapFocus(event);
        return;
      }
      if (state.closing) return;

      const tab = event.target.closest?.('[data-omri-size-guide-tab]');
      if (tab && ['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) {
        handleTabKey(tab, event);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('shopify:section:load', boot);
    document.addEventListener('shopify:section:unload', event => {
      if (!state.root) return;
      if (state.root.dataset.omriSizeGuideSourceSection !== String(event.detail?.sectionId || '')) return;
      close({ restoreFocus: false, immediate: true });
      state.root.remove();
      state.root = null;
    });
  }

  window.OmriSizeGuide = Object.freeze({ version: VERSION, boot, open, close });
  bindGlobalEvents();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();

